import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import api from '../utils/api';
import useCartStore from '../store/useCartStore';
import useWishlistStore from '../store/useWishlistStore';
import useAuthStore from '../store/useAuthStore';
import ProductCard, { KurtiPlaceholder } from '../components/ProductCard';
import { FiHeart, FiShare2, FiTruck, FiMessageCircle, FiCheck, FiChevronRight } from 'react-icons/fi';
import { Crumbs, GlassChip, GlassCard, MagneticButton, Reveal, SectionHeader, fadeUp, stagger } from '../components/ui';

const SIZES_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCartStore();
  const { toggle, isInWishlist } = useWishlistStore();
  const { user } = useAuthStore();

  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState(null);
  const [tab, setTab] = useState('description');
  const [sizeChartOpen, setSizeChartOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['product', slug],
    queryFn: () => api.get(`/products/${slug}`),
  });

  useEffect(() => {
    if (data?.product?.colors?.[0]) setSelectedColor(data.product.colors[0]);
  }, [data?.product?._id]);

  const { data: reviewsData } = useQuery({
    queryKey: ['reviews', data?.product?._id],
    queryFn: () => api.get(`/reviews/product/${data.product._id}`),
    enabled: !!data?.product?._id,
  });

  const { data: relatedData } = useQuery({
    queryKey: ['related', data?.product?.category?._id],
    queryFn: () => api.get(`/products?category=${data.product.category._id}&limit=6`),
    enabled: !!data?.product?.category?._id,
  });

  if (isLoading) return <LoadingSkeleton />;

  const p = data?.product;
  if (!p) return <NotFound />;

  const inWish = isInWishlist(p._id);
  const discount = Math.round(((p.mrp - p.price) / p.mrp) * 100);
  const orderedSizes = SIZES_ORDER.map(label => p.sizes?.find(s => s.label === label)).filter(Boolean);
  const reviews = reviewsData?.reviews || [];

  const handleAddToCart = async (buyNow = false) => {
    if (!selectedSize) { toast.error('Please select a size'); return; }
    if (!user) { navigate('/login'); return; }
    setBusy(true);
    try {
      await addToCart(p._id, selectedSize, selectedColor, 1);
      toast.success('Added to bag!');
      if (buyNow) navigate('/checkout');
    } catch (err) {
      toast.error(err.message || 'Failed to add to cart');
    }
    setBusy(false);
  };

  const handleWishlist = () => {
    if (!user) { navigate('/login'); return; }
    toggle(p._id);
    toast.success(inWish ? 'Removed from wishlist' : 'Added to wishlist');
  };

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 24px 40px' }}>
      <Crumbs items={[
        { label: 'Home', to: '/' },
        { label: p.category?.name || 'Shop', to: p.category?.slug ? `/category/${p.category.slug}` : '/' },
        { label: p.name },
      ]} />

      <div className="pp-grid" style={{ display: 'grid', gridTemplateColumns: '1.05fr 1fr', gap: 56, alignItems: 'flex-start' }}>
        {/* ─── Gallery ─── */}
        <div>
          <div style={{ position: 'sticky', top: 96 }}>
            <motion.div
              key={activeImage}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              style={{ position: 'relative', aspectRatio: '3/4', borderRadius: 24, overflow: 'hidden', background: 'var(--surface-2)', marginBottom: 12, boxShadow: '0 24px 60px rgba(42,31,26,0.08)' }}
            >
              {p.images?.[activeImage]?.url ? (
                <img src={p.images[activeImage].url} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              ) : (
                <KurtiPlaceholder color={selectedColor?.hex || '#7A1F2B'} size="100%" />
              )}
              {p.badge && (
                <span style={{ position: 'absolute', top: 18, left: 18, fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', padding: '6px 12px', background: 'rgba(122,31,43,0.95)', color: '#FFF', borderRadius: 999, backdropFilter: 'blur(8px)' }}>{p.badge}</span>
              )}
              {discount >= 10 && (
                <span style={{ position: 'absolute', top: 18, right: 18, fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', padding: '6px 12px', background: 'rgba(79,122,83,0.95)', color: '#FFF', borderRadius: 999, backdropFilter: 'blur(8px)' }}>{discount}% off</span>
              )}
            </motion.div>

            <div className="scrollx" style={{ display: 'flex', gap: 10 }}>
              {(p.images?.length > 0 ? p.images : [{ url: null }, { url: null }, { url: null }]).map((img, i) => (
                <motion.button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.96 }}
                  style={{
                    width: 78, height: 100, borderRadius: 12, padding: 0, cursor: 'pointer', flexShrink: 0, overflow: 'hidden',
                    border: i === activeImage ? '2px solid var(--ink)' : '1px solid var(--line)',
                    background: 'transparent',
                  }}
                >
                  {img.url ? <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <KurtiPlaceholder color={p.colors?.[i % (p.colors?.length || 1)]?.hex || '#7A1F2B'} />}
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        {/* ─── Info ─── */}
        <motion.div variants={stagger} initial="hidden" animate="show">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <motion.div variants={fadeUp}>
              <GlassChip tone="light">VritiEthnic · {p.category?.name}</GlassChip>
            </motion.div>
            <div style={{ display: 'flex', gap: 8 }}>
              <motion.button onClick={handleWishlist} whileTap={{ scale: 0.9 }} whileHover={{ scale: 1.06 }} style={{
                width: 40, height: 40, borderRadius: '50%', background: 'var(--surface)',
                border: '1px solid var(--line)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: inWish ? 'var(--primary)' : 'var(--ink-2)',
                boxShadow: inWish ? '0 4px 16px rgba(122,31,43,0.18)' : 'none',
              }}>
                <FiHeart size={16} fill={inWish ? 'currentColor' : 'none'} />
              </motion.button>
              <motion.button whileTap={{ scale: 0.9 }} whileHover={{ scale: 1.06 }} style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--surface)', border: '1px solid var(--line)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiShare2 size={16} />
              </motion.button>
            </div>
          </div>

          <motion.h1 variants={fadeUp} className="serif" style={{ fontSize: 'clamp(28px, 3.4vw, 42px)', lineHeight: 1.1, color: 'var(--ink)', marginTop: 12, marginBottom: 16, letterSpacing: '-0.01em' }}>
            {p.name}
          </motion.h1>

          {p.rating > 0 && (
            <motion.div variants={fadeUp} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: 'var(--gold-soft)', borderRadius: 999, fontSize: 12, fontWeight: 700, color: 'var(--gold)' }}>
                ★ {p.rating}
              </span>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{p.reviewCount} reviews</span>
              {p.soldCount > 100 && <span style={{ fontSize: 12, color: 'var(--muted)' }}>· {p.soldCount}+ sold</span>}
            </motion.div>
          )}

          <motion.div variants={fadeUp} style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 4 }}>
            <span className="serif" style={{ fontSize: 36, fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.01em' }}>₹{p.price?.toLocaleString('en-IN')}</span>
            {p.mrp > p.price && <span style={{ fontSize: 17, color: 'var(--muted)', textDecoration: 'line-through' }}>₹{p.mrp?.toLocaleString('en-IN')}</span>}
            {discount >= 10 && <span style={{ fontSize: 13, color: 'var(--ok)', fontWeight: 700, letterSpacing: '0.04em' }}>{discount}% off</span>}
          </motion.div>
          <motion.div variants={fadeUp} style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 28 }}>
            Inclusive of all taxes · 3 EMI from ₹{Math.round(p.price / 3)}/mo
          </motion.div>

          {/* Colors */}
          {p.colors?.length > 0 && (
            <motion.div variants={fadeUp} style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>
                Colour · <span style={{ fontWeight: 500, color: 'var(--muted)' }}>{selectedColor?.name}</span>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                {p.colors.map(c => {
                  const selected = selectedColor?.hex === c.hex;
                  return (
                    <motion.button
                      key={c.hex}
                      onClick={() => setSelectedColor(c)}
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.94 }}
                      title={c.name}
                      style={{
                        width: 40, height: 40, borderRadius: '50%', background: c.hex, cursor: 'pointer',
                        border: '2px solid rgba(255,255,255,0.95)',
                        outline: selected ? '2px solid var(--ink)' : '1px solid rgba(0,0,0,0.06)', outlineOffset: 2,
                        boxShadow: selected ? '0 6px 16px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.08)',
                      }}
                    />
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Sizes */}
          {orderedSizes.length > 0 && (
            <motion.div variants={fadeUp} style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                  Size · <span style={{ fontWeight: 500, color: 'var(--muted)' }}>{selectedSize || 'Select'}</span>
                </div>
                <button onClick={() => setSizeChartOpen(true)} style={{ border: 'none', background: 'transparent', fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.12em', textDecoration: 'underline', cursor: 'pointer' }}>Size chart</button>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {orderedSizes.map(s => {
                  const inStock = s.stock > 0;
                  const selected = selectedSize === s.label;
                  return (
                    <motion.button
                      key={s.label}
                      onClick={() => inStock && setSelectedSize(s.label)}
                      disabled={!inStock}
                      whileHover={inStock ? { y: -2 } : {}}
                      whileTap={inStock ? { scale: 0.96 } : {}}
                      style={{
                        minWidth: 56, height: 48, borderRadius: 12, cursor: inStock ? 'pointer' : 'not-allowed',
                        border: selected ? '2px solid var(--ink)' : '1px solid var(--line)',
                        background: selected ? 'var(--ink)' : !inStock ? 'var(--surface-2)' : 'var(--surface)',
                        color: selected ? '#FFF' : !inStock ? 'var(--muted)' : 'var(--ink)',
                        fontSize: 14, fontWeight: 700, textDecoration: !inStock ? 'line-through' : 'none', position: 'relative',
                      }}
                    >
                      {s.label}
                      {s.stock <= 5 && s.stock > 0 && <span style={{ position: 'absolute', top: -6, right: -4, fontSize: 8, fontWeight: 700, background: 'var(--warn)', color: '#FFF', padding: '2px 6px', borderRadius: 999, letterSpacing: '0.06em' }}>LOW</span>}
                    </motion.button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10 }}>Model is 5'7" wearing size M · True to size</div>
            </motion.div>
          )}

          {/* Delivery */}
          <motion.div variants={fadeUp} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', marginBottom: 20,
            background: 'rgba(255,255,255,0.55)', border: '1px solid rgba(229,219,200,0.6)', borderRadius: 14,
            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          }}>
            <span style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--gold-soft)', color: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiTruck size={16} />
            </span>
            <div style={{ flex: 1, fontSize: 13, lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--ink)' }}>Free delivery</strong> on orders above ₹999 · 3–5 business days
            </div>
          </motion.div>

          {/* CTAs */}
          <motion.div variants={fadeUp} style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
            <a href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '919999999999'}?text=${encodeURIComponent(`Hi! I'm interested in ${p.name}`)}`} target="_blank" rel="noreferrer" style={{
              width: 56, height: 56, borderRadius: 16, background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#25D366', textDecoration: 'none', flexShrink: 0,
            }} title="WhatsApp">
              <FiMessageCircle size={20} />
            </a>
            <MagneticButton variant="ghost" size="lg" onClick={() => handleAddToCart(false)} disabled={busy} style={{ flex: 1, height: 56 }}>
              Add to bag
            </MagneticButton>
            <MagneticButton variant="primary" size="lg" onClick={() => handleAddToCart(true)} disabled={busy} style={{ flex: 1.2, height: 56 }}>
              Buy now ›
            </MagneticButton>
          </motion.div>

          {/* Coupons */}
          <motion.div variants={fadeUp} style={{
            background: 'linear-gradient(135deg, var(--gold-soft) 0%, rgba(239,228,200,0.5) 100%)',
            border: '1px solid rgba(176,138,62,0.2)', borderRadius: 16, padding: '16px 18px', marginBottom: 28,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 12 }}>Available Offers</div>
            {[['VRITI10', '10% off on first order'], ['COTTON199', 'Extra ₹199 off on cotton kurtis']].map(([code, desc]) => (
              <div key={code} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <span style={{ border: '1.5px dashed var(--gold)', padding: '4px 10px', fontSize: 11, fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.08em', borderRadius: 4 }}>{code}</span>
                <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{desc}</span>
              </div>
            ))}
          </motion.div>

          {/* Tabs */}
          <div>
            <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--line-2)', marginBottom: 24 }}>
              {['description', 'details', `reviews (${p.reviewCount || 0})`].map(t => {
                const key = t.split(' ')[0];
                return (
                  <button key={t} onClick={() => setTab(key)} className={`ve-tab ${tab === key ? 'active' : ''}`}>
                    {t}
                  </button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                {tab === 'description' && (
                  <div style={{ fontSize: 14, lineHeight: 1.8, color: 'var(--ink-2)' }}>{p.description}</div>
                )}
                {tab === 'details' && p.details && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                    {Object.entries(p.details).filter(([_, v]) => v).map(([k, v]) => (
                      <div key={k} style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.5)', borderRadius: 10, border: '1px solid rgba(229,219,200,0.5)' }}>
                        <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>{k}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{v}</div>
                      </div>
                    ))}
                  </div>
                )}
                {tab === 'reviews' && (
                  <ReviewsSection reviews={reviews} rating={p.rating} reviewCount={p.reviewCount} />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* Related */}
      {relatedData?.products?.length > 0 && (
        <section style={{ marginTop: 80 }}>
          <SectionHeader kicker="You may also like" title="More from this edit" hindi="और भी" />
          <Reveal>
            <div className="pp-related-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 22 }}>
              {relatedData.products.filter(rp => rp._id !== p._id).slice(0, 4).map(rp => (
                <motion.div key={rp._id} whileHover={{ y: -6 }} transition={{ duration: 0.25 }}>
                  <ProductCard product={rp} />
                </motion.div>
              ))}
            </div>
          </Reveal>
        </section>
      )}

      {/* Size chart modal */}
      <AnimatePresence>
        {sizeChartOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSizeChartOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(26,16,14,0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          >
            <motion.div
              onClick={e => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="ve-glass-form-card"
              style={{ padding: 32, maxWidth: 520, width: '100%' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <h2 className="serif" style={{ fontSize: 26 }}>Size Chart</h2>
                <button onClick={() => setSizeChartOpen(false)} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: 'var(--muted)', lineHeight: 1 }}>✕</button>
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 18 }}>Measurements in inches. Add 2" to bust for comfort fit.</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Size', 'Bust', 'Waist', 'Length', 'Stock'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '12px 14px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--muted)', fontWeight: 700, background: 'var(--surface-2)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orderedSizes.map(s => (
                    <tr key={s.label} style={{ borderTop: '1px solid var(--line-2)', opacity: s.stock > 0 ? 1 : 0.55 }}>
                      <td style={{ padding: '14px', fontWeight: 700 }}>{s.label}</td>
                      <td style={{ padding: '14px' }}>{s.bust}"</td>
                      <td style={{ padding: '14px' }}>{s.waist}"</td>
                      <td style={{ padding: '14px' }}>{s.length}"</td>
                      <td style={{ padding: '14px' }}>
                        <span style={{ color: s.stock > 0 ? 'var(--ok)' : 'var(--danger)', fontWeight: 700, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                          {s.stock > 0 ? 'In stock' : 'Out'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 900px) {
          .pp-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .pp-related-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </div>
  );
}

function ReviewsSection({ reviews, rating, reviewCount }) {
  const dist = [5, 4, 3, 2, 1].map(star => {
    if (!reviews?.length) return 0;
    const count = reviews.filter(r => Math.round(r.rating) === star).length;
    return Math.round((count / reviews.length) * 100);
  });
  return (
    <div>
      {reviewCount > 0 && (
        <GlassCard hover={false} style={{ padding: 24, marginBottom: 28 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 28, alignItems: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <div className="serif" style={{ fontSize: 56, lineHeight: 1, color: 'var(--ink)' }}>{rating}</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 2, margin: '8px 0' }}>
                {[1,2,3,4,5].map(i => <span key={i} style={{ color: i <= Math.round(rating) ? 'var(--gold)' : 'var(--line)', fontSize: 14 }}>★</span>)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{reviewCount} reviews</div>
            </div>
            <div>
              {dist.map((v, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, fontSize: 11 }}>
                  <span style={{ width: 12 }}>{5-i}</span>
                  <span style={{ color: 'var(--gold)', fontSize: 10 }}>★</span>
                  <div style={{ flex: 1, height: 8, background: 'var(--surface-2)', borderRadius: 4, overflow: 'hidden' }}>
                    <motion.div initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ duration: 0.8, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }} style={{ height: '100%', background: 'linear-gradient(90deg, var(--gold), #C9A85C)' }} />
                  </div>
                  <span style={{ width: 32, textAlign: 'right', color: 'var(--muted)' }}>{v}%</span>
                </div>
              ))}
            </div>
          </div>
        </GlassCard>
      )}
      {reviews.map((r, i) => (
        <motion.div key={r._id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}
          style={{ paddingBottom: 22, borderBottom: '1px solid var(--line-2)', marginBottom: 22 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, var(--primary), var(--primary-2))', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 }}>{r.user?.name?.[0] || 'U'}</span>
              <div>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{r.user?.name || 'Anonymous'}</span>
                {r.isVerified && <span style={{ fontSize: 10, color: 'var(--ok)', fontWeight: 700, marginLeft: 8, letterSpacing: '0.06em', textTransform: 'uppercase' }}><FiCheck size={10} style={{ display: 'inline', verticalAlign: 'middle' }} /> Verified</span>}
              </div>
            </div>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>{new Date(r.createdAt).toLocaleDateString('en-IN')}</span>
          </div>
          <div style={{ display: 'flex', gap: 2, marginBottom: 8 }}>
            {[1,2,3,4,5].map(i => <span key={i} style={{ color: i <= r.rating ? 'var(--gold)' : 'var(--line)', fontSize: 14 }}>★</span>)}
          </div>
          <div className="serif" style={{ fontSize: 17, marginBottom: 6 }}>{r.title}</div>
          <div style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--ink-2)' }}>{r.body}</div>
          {r.size && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 10, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Bought size {r.size}{r.height ? ` · ${r.height}` : ''}</div>}
        </motion.div>
      ))}
      {reviews.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)', fontSize: 13 }}>
          No reviews yet. Be the first to review.
        </div>
      )}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56 }}>
      <div className="ve-skeleton" style={{ aspectRatio: '3/4', borderRadius: 24 }} />
      <div>
        <div className="ve-skeleton" style={{ height: 16, width: 140, marginBottom: 16 }} />
        <div className="ve-skeleton" style={{ height: 40, width: '80%', marginBottom: 12 }} />
        <div className="ve-skeleton" style={{ height: 40, width: '60%', marginBottom: 24 }} />
        <div className="ve-skeleton" style={{ height: 60, width: '100%' }} />
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div style={{ minHeight: 480, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
      <h2 className="serif" style={{ fontSize: 32 }}>Product not found</h2>
      <MagneticButton to="/" variant="primary">Back to shop</MagneticButton>
    </div>
  );
}
